import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Editor, Range } from '@tiptap/core';
import React from 'react';
import ReactDOM from 'react-dom/client';
import CommandMenuSimple, { CommandMenuItem } from './CommandMenuSimple';

// Define the command items
const getCommandItems = (): CommandMenuItem[] => {
  return [
    { 
      title: 'Text', 
      command: ({ editor, range }) => {
        // Delete the "/" and any query, then create a paragraph
        editor.chain().focus().deleteRange(range).setParagraph().run();
      }
    },
    { 
      title: 'Heading 1', 
      command: ({ editor, range }) => {
        // Delete the "/" and create a heading
        editor.chain().focus().deleteRange(range).setHeading({ level: 1 }).run();
      }
    },
    { 
      title: 'Heading 2', 
      command: ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).setHeading({ level: 2 }).run();
      }
    },
    { 
      title: 'Heading 3', 
      command: ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).setHeading({ level: 3 }).run();
      }
    },
    { 
      title: 'Bulleted list', 
      command: ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).toggleBulletList().run();
      }
    },
    { 
      title: 'Numbered list', 
      command: ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).toggleOrderedList().run();
      }
    },
    { 
      title: 'To-do list', 
      command: ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).toggleTaskList().run();
      }
    },
    { 
      title: 'Quote', 
      command: ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).toggleBlockquote().run();
      }
    },
    { 
      title: 'Divider', 
      command: ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).setHorizontalRule().run();
      }
    },
    { 
      title: 'Code', 
      command: ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).setCodeBlock().run();
      }
    },
  ];
};

class CommandMenuManager {
  private container: HTMLDivElement | null = null;
  private component: ReactDOM.Root | null = null;
  private isActive = false;
  private currentRange: Range | null = null;
  private currentEditor: Editor | null = null;
  private query = '';
  private clickOutsideHandler: ((event: MouseEvent) => void) | null = null;

  show(editor: Editor, range: Range, query: string = '') {
    this.currentEditor = editor;
    this.currentRange = range;
    this.query = query;
    this.isActive = true;

    const items = getCommandItems(); // Show all items, filtering will be done in the component

    if (!this.container) {
      this.container = document.createElement('div');
      this.container.className = 'command-menu-portal';
      this.container.style.pointerEvents = 'auto';
      document.body.appendChild(this.container);
    }

    if (!this.component) {
      this.component = ReactDOM.createRoot(this.container);
    }

    this.component.render(
      <CommandMenuSimple
        items={items}
        editor={editor}
        range={range}
        query={query}
        onSelect={(item) => {
          if (this.currentEditor && this.currentRange) {
            item.command({ editor: this.currentEditor, range: this.currentRange });
            this.hide();
          }
        }}
        onClose={() => {
          this.hide();
        }}
      />
    );

    this.updatePosition(editor, range);
    this.setupClickOutsideHandler();
  }

  private setupClickOutsideHandler() {
    // Remove existing handler if any
    if (this.clickOutsideHandler) {
      document.removeEventListener('mousedown', this.clickOutsideHandler);
    }

    this.clickOutsideHandler = (event: MouseEvent) => {
      if (this.container && event.target instanceof Node && !this.container.contains(event.target)) {
        this.hide();
      }
    };

    // Add the handler immediately but after the current execution stack
    requestAnimationFrame(() => {
      if (this.clickOutsideHandler && this.isActive) {
        document.addEventListener('mousedown', this.clickOutsideHandler);
      }
    });
  }

  private updatePosition(editor: Editor, range: Range) {
    if (!this.container) return;

    const { view } = editor;
    const { from } = range;
    const start = view.coordsAtPos(from);

    // Position the menu below the cursor
    this.container.style.position = 'absolute';
    this.container.style.left = `${start.left}px`;
    this.container.style.top = `${start.bottom + window.scrollY}px`;
  }

  hide() {
    this.isActive = false;
    this.currentRange = null;
    this.currentEditor = null;
    this.query = '';

    // Remove click outside handler
    if (this.clickOutsideHandler) {
      document.removeEventListener('mousedown', this.clickOutsideHandler);
      this.clickOutsideHandler = null;
    }

    if (this.component) {
      this.component.unmount();
      this.component = null;
    }
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
      this.container = null;
    }
  }

  updateQuery(editor: Editor, range: Range, query: string) {
    if (this.isActive) {
      this.show(editor, range, query);
    }
  }

  handleKeyDown(event: KeyboardEvent): boolean {
    // Keyboard handling is now done in the CommandMenuSimple component
    // We just need to return false to let the component handle it
    return false;
  }

  getIsActive() {
    return this.isActive;
  }
}

const commandMenuManager = new CommandMenuManager();

const CommandMenuExtension = Extension.create({
  name: 'commandMenu',

  addProseMirrorPlugins() {
    const extension = this;
    
    return [
      new Plugin({
        key: new PluginKey('commandMenu'),
        
        props: {
          handleKeyDown(view, event) {
            if (commandMenuManager.getIsActive()) {
              return commandMenuManager.handleKeyDown(event);
            }

            if (event.key === '/') {
              const { state } = view;
              const { selection } = state;
              const { from } = selection;
              
              // Show menu immediately after typing '/'
              setTimeout(() => {
                const range = { from, to: from + 1 };
                commandMenuManager.show(extension.editor, range, '');
              }, 0);
              return false; // Allow the '/' to be typed first
            }

            return false;
          },

          handleTextInput(view, from, to, text) {
            if (commandMenuManager.getIsActive() && text !== '/') {
              const { state } = view;
              const currentPos = state.selection.from;
              const $pos = state.doc.resolve(currentPos);
              const textBefore = $pos.parent.textContent.slice(0, $pos.parentOffset);
              const slashIndex = textBefore.lastIndexOf('/');
              
              if (slashIndex !== -1) {
                const query = textBefore.slice(slashIndex + 1) + text;
                const range = { from: slashIndex + $pos.start(), to: currentPos + text.length };
                commandMenuManager.updateQuery(extension.editor, range, query);
              }
            }
            return false;
          }
        }
      })
    ];
  }
});


export default CommandMenuExtension;